import { ClaimAnimation, loadClaimAsset, observeClaimPlayback } from "./claim-lottie-asset";

interface ClaimAnimationModal {
  present(): Promise<void>;
  dismiss(): Promise<boolean>;
  onDidDismiss(): Promise<any>;
  querySelector(selector: string): Element | null;
  remove(): void;
}

export async function playClaimAnimation(
  ref: ClaimAnimation,
  controller: AbortController,
  createModal: (canvas: HTMLElement, close: () => void) => Promise<ClaimAnimationModal>
): Promise<boolean> {
  const signal = controller.signal;
  if (signal.aborted || !globalThis.crypto?.subtle) return false;
  let loadTimer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    loadTimer = setTimeout(() => { controller.abort(); reject(new Error("asset_timeout")); }, 3500);
  });
  let data, lottie;
  try {
    [data, lottie] = await Promise.race([Promise.all([loadClaimAsset(ref, signal), import("lottie-web/build/player/lottie_light_canvas")]), timeout]);
  } catch { return false; }
  finally { clearTimeout(loadTimer); }
  if (signal.aborted) return false;

  // Prove visible artwork before opening the existing Ionic overlay. The canvas
  // is paused at that frame and then moved into the fullscreen popup unchanged.
  const probe = document.createElement("div"), canvas = document.createElement("div");
  probe.style.cssText = "position:fixed;top:0;left:0;width:min(300px,calc(100vw - 48px));height:min(300px,40vh);visibility:hidden;pointer-events:none";
  probe.setAttribute("aria-hidden", "true");
  canvas.style.cssText = "width:100%;height:100%";
  probe.append(canvas);
  let player: any, modal: ClaimAnimationModal, playbackTimer: ReturnType<typeof setTimeout>;
  let painted = false, outcome: boolean | undefined;
  let ready: (ok: boolean) => void, finish: (ok: boolean) => void;
  const firstPaint = new Promise<boolean>(resolve => { ready = resolve; });
  const finished = new Promise<boolean>(resolve => {
    finish = ok => {
      if (outcome !== undefined) return;
      outcome = ok;
      ready(false);
      resolve(ok);
    };
  });
  const cancel = () => finish(false);
  signal.addEventListener("abort", cancel, { once: true });
  try {
    document.body.append(probe);
    player = lottie.default.loadAnimation({ container: canvas, renderer: "canvas", loop: false, autoplay: false, autoloadSegments: false, animationData: data });
    playbackTimer = setTimeout(cancel, 2000 + 1000 * (data.op - data.ip) / data.fr);
    observeClaimPlayback(player, canvas, () => {
      if (signal.aborted) return cancel();
      painted = true;
      player.pause();
      ready(true);
    }, ok => {
      if (!ok) return cancel();
      // Keep the final frame and receipt text readable until the user closes it.
      clearTimeout(playbackTimer);
    });
    if (!painted) player.play();
    if (!await firstPaint || signal.aborted || outcome !== undefined) return false;
    modal = await createModal(canvas, () => finish(true));
    if (signal.aborted || outcome !== undefined) return false;
    modal.onDidDismiss().then(() => finish(true), cancel);
    await modal.present();
    if (signal.aborted || outcome !== undefined) return outcome === true;
    (modal.querySelector(".coupon-claim-close") as HTMLElement)?.focus();
    player.resize();
    player.play();
    return await finished;
  } catch { return false; }
  finally {
    clearTimeout(playbackTimer);
    signal.removeEventListener("abort", cancel);
    try { player?.destroy(); }
    finally {
      try { await modal?.dismiss(); } catch { /* The overlay may already be gone. */ }
      modal?.remove();
      probe.remove();
    }
  }
}
